import React, { useEffect, useRef, useState } from "react";
import {
    getAdminGroupConfigs,
    saveIsActive,
    saveIsDom,
    saveIsSondage,
    saveMsgGroup,
} from "../api/appliApi";
import { Button, Form } from "react-bootstrap";
import { FormModal } from "../components/FormModal";
import { ToastContainer, toast } from "react-toastify";
import adminIllustration from "../asset/img/admin-illustration.png";
import { AdminGroupConfig, GroupOption } from "../utils/Type";

const AdminMessage: React.FC = () => {
    const [groupOptions, setGroupOptions] = useState<GroupOption[]>([]);
    const [selectedGroupIds, setSelectedGroupIds] = useState<number[]>([]);
    const [datas, setDatas] = useState<{ [gid: number]: string }>({});
    const [formDataByGroup, setFormDataByGroup] = useState<{
        [gid: number]: { adminMessage: string };
    }>({});
    const [modalShowByGroup, setModalShowByGroup] = useState<{
        [gid: number]: boolean;
    }>({});
    const [formDataActiveByGroup, setFormDataActiveByGroup] = useState<{
        [gid: number]: { isActiveMessage: boolean };
    }>({});
    const [formDataSondageByGroup, setFormDataSondageByGroup] = useState<{
        [gid: number]: { isActivatedSondage: boolean };
    }>({});
    const [formDataDomByGroup, setFormDataDomByGroup] = useState<{
        [gid: number]: { isActivatedDom: boolean };
    }>({});
    const submitLockRef = useRef(false);
    const [isSaving, setIsSaving] = useState(false);

    useEffect(() => {
        (async () => {
            try {
                const resp = await getAdminGroupConfigs();
                const configs: AdminGroupConfig[] = Array.isArray(resp?.data) ? resp.data : [];
                setGroupOptions(configs.map(({ id, name }) => ({ id, name })));

                const dataByGroup: { [gid: number]: string } = {};
                const activeByGroup: { [gid: number]: { isActiveMessage: boolean } } = {};
                const sondageByGroup: { [gid: number]: { isActivatedSondage: boolean } } = {};
                const domByGroup: { [gid: number]: { isActivatedDom: boolean } } = {};

                configs.forEach((group) => {
                    dataByGroup[group.id] = group.adminMessage ?? "";
                    activeByGroup[group.id] = { isActiveMessage: !!group.isActiveMessage };
                    sondageByGroup[group.id] = { isActivatedSondage: !!group.isActivatedSondage };
                    domByGroup[group.id] = { isActivatedDom: !!group.isActivatedDom };
                });

                setDatas(dataByGroup);
                setFormDataActiveByGroup(activeByGroup);
                setFormDataSondageByGroup(sondageByGroup);
                setFormDataDomByGroup(domByGroup);

                const stored = JSON.parse(localStorage.getItem("groupes") || "[]");
                const initial = Array.isArray(stored) && stored.length
                    ? (stored as number[])
                    : configs.map((g: GroupOption) => g.id);
                setSelectedGroupIds(initial);
            } catch {
                toast.error("Erreur chargement groupes");
            }
        })();
    }, []);

    useEffect(() => {
        localStorage.setItem("groupes", JSON.stringify(selectedGroupIds));
    }, [selectedGroupIds]);

    const notify = (msg: string) => toast(msg);
    const notifyErr = (msg: string) => toast.error(msg);

    const handleChangeActiveGroup = async (gid: number, checked: boolean) => {
        setFormDataActiveByGroup((p) => ({
            ...p,
            [gid]: { isActiveMessage: checked },
        }));
        try {
            await saveIsActive.post({ groupId: gid, isActiveMessage: checked });
            notify("État " + (checked ? ("actif du message") : ("inactif du message")) + " enregistré");
        } catch {
            notifyErr("Erreur lors de l'activation");
        }
    };

    const handleChangeSondageGroup = async (gid: number, checked: boolean) => {
        setFormDataSondageByGroup((p) => ({
            ...p,
            [gid]: { isActivatedSondage: checked },
        }));
        try {
            await saveIsSondage.post({ groupId: gid, isActivatedSondage: checked });
            notify("État " + (checked ? ("actif du sondage") : ("inactif du sondage")) + " enregistré");
        } catch {
            notifyErr("Erreur lors du sondage");
        }
    };

    const handleChangeDomGroup = async (gid: number, checked: boolean) => {
        setFormDataDomByGroup((p) => ({
            ...p,
            [gid]: { isActivatedDom: checked },
        }));
        try {
            await saveIsDom.post({ groupId: gid, isActivatedDom: checked });
            notify("État " + (checked ? ("actif du domiciliation bancaire") : ("inactif du domiciliation bancaire")) + " enregistré");
        } catch {
            notifyErr("Erreur lors du domiciliation bancaire");
        }
    };

    const handleEditGroup = (gid: number) => {
        setFormDataByGroup((p) => ({
            ...p,
            [gid]: { adminMessage: datas[gid] || "" },
        }));
        setModalShowByGroup((p) => ({ ...p, [gid]: true }));
    };

    const handleChangeGroup = (gid: number, msg: string) => {
        setFormDataByGroup((p) => ({
            ...p,
            [gid]: { adminMessage: msg },
        }));
    };

    const handleValidateGroup = async (gid: number) => {
        if (submitLockRef.current || isSaving) return;

        submitLockRef.current = true;
        setIsSaving(true);

        const msg = formDataByGroup[gid]?.adminMessage || "";
        try {
            await saveMsgGroup.post({ groupId: gid, adminMessage: msg });
            setModalShowByGroup((p) => ({ ...p, [gid]: false }));
            setDatas((d) => ({ ...d, [gid]: msg }));
            notify("Message enregistré");
        } catch {
            notifyErr("Erreur lors de l'enregistrement du message");
        } finally {
            setIsSaving(false);
            submitLockRef.current = false;
        }
    };

    return (
        <>
            <div className="custom-content position-relative">
                <div className="custom-header">
                    <span className="custom-title">Administration</span>
                </div>

                <ToastContainer />

                {selectedGroupIds.map((gid) => {
                    const group = groupOptions.find((g) => g.id === gid);
                    if (!group) return null;
                    return (
                        <div key={gid} className="mb-5 p-3 border rounded bg-light">
                            <div className="d-flex align-items-center">
                                <h4 className="mb-0 me-3">Message pour le groupe "{group.name}"</h4>
                                <Form.Check
                                    type="switch"
                                    id={`switch-active-${gid}`}
                                    checked={formDataActiveByGroup[gid]?.isActiveMessage || false}
                                    onChange={(e) =>
                                        handleChangeActiveGroup(gid, e.target.checked)
                                    }
                                />
                            </div>

                            <i>
                                Cliquez sur modifier pour envoyer un message à votre équipe à
                                l'ouverture de leur application.
                            </i>

                            <div className="bg-white p-4 rounded my-3 border">
                                {datas[gid] ? <b>"{datas[gid]}"</b> : <i>Aucun message défini actuellement</i>}
                            </div>

                            <Button onClick={() => handleEditGroup(gid)}>Modifier</Button>

                            <div className="mt-3">
                                <Form.Group className="d-flex align-items-start gap-4">
                                    <Form.Label>Sondage</Form.Label>
                                    <Form.Check
                                        type="switch"
                                        id={`switch-sondage-${gid}`}
                                        checked={
                                            formDataSondageByGroup[gid]?.isActivatedSondage || false
                                        }
                                        onChange={(e) =>
                                            handleChangeSondageGroup(gid, e.target.checked)
                                        }
                                    />
                                </Form.Group>
                            </div>
                            <i className="d-block mb-3">
                                L'activation déclenche un questionnaire de satisfaction client
                                sur l'application technicien.
                            </i>
                            <div className="mt-3">
                                <Form.Group className="d-flex align-items-start gap-4">
                                    <Form.Label>Collecter le numéro commerçant CB (Domiciliation) ?</Form.Label>
                                    <Form.Check
                                        type="switch"
                                        id={`switch-dom-${gid}`}
                                        checked={
                                            formDataDomByGroup[gid]?.isActivatedDom || false
                                        }
                                        onChange={(e) =>
                                            handleChangeDomGroup(gid, e.target.checked)
                                        }
                                    />
                                </Form.Group>
                            </div>

                            <FormModal
                                show={modalShowByGroup[gid] || false}
                                onValidate={() => handleValidateGroup(gid)}
                                onAbort={() => {
                                    if (isSaving) return;
                                    setModalShowByGroup((p) => ({ ...p, [gid]: false }))
                                }}
                                title={`Message pour le groupe ${group.name}`}
                                isBusy={isSaving}
                                btnValidate={isSaving ? "Enregistrement..." : "Enregistrer"}
                                abortDisabled={isSaving}
                                body={
                                    <Form>
                                        <Form.Group
                                            className="mb-3"
                                            controlId={`msg.ControlInput-${gid}`}
                                        >
                                            <Form.Label>Votre message :</Form.Label>
                                            <Form.Control
                                                as="textarea"
                                                placeholder="Votre message"
                                                value={formDataByGroup[gid]?.adminMessage || ""}
                                                onChange={(e) =>
                                                    handleChangeGroup(gid, e.target.value)
                                                }
                                                maxLength={250}
                                                style={{ height: "100px" }}
                                                autoFocus
                                            />
                                        </Form.Group>
                                        {(formDataByGroup[gid]?.adminMessage?.length || 0) >= 250 && (
                                            <div className="text-danger mt-1">
                                                Limite atteinte : vous ne pouvez pas saisir plus de 250 caractères.
                                            </div>
                                        )}
                                    </Form>
                                }
                            />
                        </div>
                    );
                })}
            </div>
            <img
                src={adminIllustration}
                alt="Illustration"
                style={{
                    width: "390px",
                    height: "373px",
                    marginLeft: "-20px"
                }}
            />
        </>
    );

};

export default AdminMessage;
